/**
 * Formulario «Subida artículo» → POST JSON a offsiders/create-post
 * Listado del autor → GET offsiders/posts-by-author (query: hash, authorName)
 * Detalle → GET offsiders/get-post (query: hash, articleId, authorName)
 * Edición → PUT JSON a offsiders/update-post
 * Nota: fetch no permite body en GET; el hash va en query.
 * @version 2026-07-27-v20
 */

const DEFAULT_ENDPOINT = 'https://shopify-webapps-pro.dev-lab.es/offsiders/create-post';
const DEFAULT_POSTS_ENDPOINT = 'https://shopify-webapps.dev-lab.es/offsiders/posts-by-author';
const DEFAULT_GET_POST_ENDPOINT = 'https://shopify-webapps.dev-lab.es/offsiders/get-post';
const DEFAULT_UPDATE_ENDPOINT = 'https://shopify-webapps.dev-lab.es/offsiders/update-post';
const DEFAULT_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const HASH_UNAUTHORIZED_MESSAGE =
  'No autorizado: el hash no coincide. Comprueba authorName (exacto) y que el ID sea gid://shopify/Shop/…';
const FETCH_NETWORK_MESSAGE =
  'CORS en get-post: la petición llega (a veces 200) pero el navegador bloquea la respuesta. En el API hay que repetir en get-post las mismas cabeceras Access-Control-Allow-Origin que ya tiene posts-by-author.';

/**
 * @typedef {{ title: string; tag: string; logo?: string | null }} TeamOption
 */

/**
 * @typedef {{ name: string; type: string; data: string }} ArticleImagePayload
 */

/**
 * @typedef {{ id?: string; articleId?: string; title?: string; handle?: string; url?: string; body?: string; authorName?: string }} AuthorPost
 */

/**
 * @typedef {{ url?: string; title?: string; body?: string }} ArticleMeta
 */

/**
 * @param {string} endpoint
 * @param {Record<string, string>} params
 * @returns {string}
 */
function buildUrlWithQuery(endpoint, params) {
  const url = new URL(endpoint);
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

/**
 * GET “simple” (sin headers custom) para evitar preflight OPTIONS.
 * El hash/params van en query porque fetch no permite body en GET.
 *
 * @param {string} endpoint
 * @param {Record<string, string>} params
 * @param {AbortSignal | undefined} [signal]
 * @returns {Promise<Response>}
 */
function fetchGetWithQuery(endpoint, params, signal) {
  return fetch(buildUrlWithQuery(endpoint, params), {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    signal,
  });
}

/**
 * @param {unknown} error
 * @param {string} fallback
 * @returns {string}
 */
function formatFetchError(error, fallback) {
  if (!(error instanceof Error) || !error.message) {
    return fallback;
  }
  if (/failed to fetch|networkerror|load failed/i.test(error.message)) {
    return FETCH_NETWORK_MESSAGE;
  }
  return error.message;
}

/**
 * @param {Response} response
 * @returns {Promise<Record<string, unknown>>}
 */
async function parseJsonResponse(response) {
  const responseText = await response.text();
  if (!responseText) {
    return {};
  }

  try {
    const parsed = JSON.parse(responseText);
    return parsed && typeof parsed === 'object' ? parsed : { message: responseText };
  } catch {
    return { message: responseText };
  }
}

/**
 * @param {Record<string, unknown>} responseData
 * @param {number} status
 * @returns {never}
 */
function throwApiError(responseData, status) {
  const apiMessage =
    typeof responseData.message === 'string'
      ? responseData.message
      : typeof responseData.error === 'string'
        ? responseData.error
        : null;
  if (status === 401) {
    throw new Error(apiMessage ?? HASH_UNAUTHORIZED_MESSAGE);
  }
  throw new Error(apiMessage ?? `Error ${status}`);
}

/**
 * @param {() => boolean} check
 * @param {number} timeoutMs
 * @returns {Promise<void>}
 */
function waitForDependency(check, timeoutMs) {
  return new Promise((resolve) => {
    if (check()) {
      resolve();
      return;
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (check() || Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(timer);
        resolve();
      }
    }, 40);
  });
}

/**
 * @param {string} html
 * @returns {string}
 */
/**
 * @param {string} text
 * @returns {string}
 */
function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * get-post puede devolver body en texto plano (con \n) o HTML.
 * @param {string} body
 * @returns {string}
 */
function articleBodyToHtml(body) {
  const text = String(body ?? '').trim();
  if (!text) return '';
  if (/<[a-z][\s\S]*>/i.test(text)) {
    return text;
  }

  return text
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * @param {Record<string, unknown>} responseData
 * @returns {Record<string, unknown>}
 */
function pickArticlePayload(responseData) {
  const candidates = [
    responseData,
    responseData.data,
    responseData.post,
    responseData.article,
    responseData.result,
  ];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      continue;
    }
    const row = /** @type {Record<string, unknown>} */ (candidate);
    if (
      typeof row.body === 'string' ||
      typeof row.body_html === 'string' ||
      typeof row.content === 'string'
    ) {
      return row;
    }
  }

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      continue;
    }
    const row = /** @type {Record<string, unknown>} */ (candidate);
    if (
      typeof row.title === 'string' &&
      (typeof row.id === 'string' || typeof row.articleId === 'string')
    ) {
      return row;
    }
  }

  return responseData;
}

/**
 * @param {Record<string, unknown>} article
 * @returns {string}
 */
function readArticleBody(article) {
  if (typeof article.body === 'string' && article.body.trim()) {
    return articleBodyToHtml(article.body);
  }
  if (typeof article.body_html === 'string' && article.body_html.trim()) {
    return articleBodyToHtml(article.body_html);
  }
  if (typeof article.content === 'string' && article.content.trim()) {
    return articleBodyToHtml(article.content);
  }
  return '';
}

function stripHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

/**
 * @param {string} text
 * @returns {number}
 */
function countWords(text) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/**
 * @param {string} storeId
 * @returns {string}
 */
function normalizeStoreId(storeId) {
  const trimmed = String(storeId).trim();
  if (/^\d+$/.test(trimmed)) {
    return `gid://shopify/Shop/${trimmed}`;
  }
  return trimmed;
}

/**
 * @param {string} authorName
 * @param {string} storeId
 * @returns {Promise<string>}
 */
async function buildAuthorHash(authorName, storeId) {
  const payload = `${authorName.trim()}##${normalizeStoreId(storeId)}`;
  const data = new TextEncoder().encode(payload);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * @param {string} gid
 * @returns {string}
 */
function extractArticleNumericId(gid) {
  const match = String(gid).match(/Article\/(\d+)/i);
  return match?.[1] ?? '';
}

/**
 * @param {string} text
 * @returns {string | null}
 */
function extractFirstImageUrl(text) {
  const match = text.match(/https?:\/\/[^\s<>"']+\.(?:png|jpe?g|gif|webp|svg)(?:\?[^\s<>"']*)?/i);
  return match ? match[0] : null;
}

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('No se pudo leer el archivo'));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * @param {string} dataUrl
 * @returns {{ name: string; type: string; data: string } | null}
 */
function parseDataImageUrl(dataUrl) {
  const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) {
    return null;
  }

  return {
    name: 'imagen-incrustada',
    type: match[1],
    data: match[2],
  };
}

/**
 * @param {File} file
 * @returns {Promise<ArticleImagePayload>}
 */
async function fileToImagePayload(file) {
  const dataUrl = await readFileAsDataUrl(file);
  const parsed = parseDataImageUrl(dataUrl);
  if (!parsed) {
    throw new Error('Formato de imagen no válido');
  }

  return {
    name: file.name || parsed.name,
    type: file.type || parsed.type,
    data: parsed.data,
  };
}

/**
 * @param {string} text
 * @param {number} maxLength
 * @returns {string}
 */
function truncateText(text, maxLength) {
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 1).trim()}…`;
}

class ArticleQuillEditor {
  /** @type {HTMLElement | null} */
  #root = null;

  /** @type {HTMLElement | null} */
  #host = null;

  /** @type {HTMLTextAreaElement | null} */
  #output = null;

  /** @type {(() => void) | null} */
  #onChange = null;

  /** @type {unknown} */
  #quill = null;

  /** @type {Map<string, ArticleImagePayload>} */
  #imageStore = new Map();

  /** @type {number} */
  #maxImageBytes = DEFAULT_MAX_IMAGE_BYTES;

  /** @type {string} */
  #imageTooLargeMessage = 'La imagen no puede superar 5 MB.';

  /**
   * @param {HTMLElement} root
   * @param {{ onChange?: () => void; maxImageBytes?: number; imageTooLargeMessage?: string }} [options]
   */
  constructor(root, options = {}) {
    this.#root = root;
    this.#host = root.querySelector('[data-quill-host]');
    this.#output = root.querySelector('[data-rich-editor-output]');
    this.#onChange = options.onChange ?? null;
    this.#maxImageBytes = options.maxImageBytes ?? DEFAULT_MAX_IMAGE_BYTES;
    this.#imageTooLargeMessage = options.imageTooLargeMessage ?? this.#imageTooLargeMessage;

    if (!(this.#host instanceof HTMLElement) || !(this.#output instanceof HTMLTextAreaElement)) {
      return;
    }

    if (typeof Quill === 'undefined') {
      root.dataset.editorState = 'quill-missing';
      return;
    }

    this.#quill = new Quill(this.#host, {
      theme: 'snow',
      modules: {
        toolbar: [
          [{ header: [2, 3, 4, false] }],
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'image'],
        ],
      },
      placeholder: this.#host.dataset.placeholder || '',
    });

    /** @type {{ on: (event: string, handler: () => void) => void; getModule: (name: string) => { addHandler?: (name: string, handler: () => void) => void } | undefined; getSelection: (focus?: boolean) => { index: number } | null; getLength: () => number; insertEmbed: (index: number, type: string, value: string, source?: string) => void; setSelection: (index: number) => void; getSemanticHTML?: () => string; root: HTMLElement; getText: () => string; setContents: (value: unknown[]) => void }} */
    const quill = this.#quill;

    const toolbar = quill.getModule('toolbar');
    toolbar?.addHandler?.('image', () => {
      this.#pickImage();
    });

    quill.on('text-change', () => {
      this.#sync();
    });

    this.#sync(false);
  }

  #pickImage() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.hidden = true;
    document.body.appendChild(input);

    input.addEventListener(
      'change',
      () => {
        const file = input.files?.[0];
        input.remove();
        if (file) {
          void this.#insertImageFile(file);
        }
      },
      { once: true }
    );

    input.click();
  }

  /**
   * @param {File} file
   */
  async #insertImageFile(file) {
    if (!this.#quill) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      window.alert('Solo puedes insertar archivos de imagen.');
      return;
    }

    if (file.size > this.#maxImageBytes) {
      window.alert(this.#imageTooLargeMessage);
      return;
    }

    try {
      const payload = await fileToImagePayload(file);
      const dataUrl = `data:${payload.type};base64,${payload.data}`;
      this.#imageStore.set(dataUrl, payload);

      const quill = this.#quill;
      const range = quill.getSelection(true);
      const index = range?.index ?? Math.max(0, quill.getLength() - 1);
      quill.insertEmbed(index, 'image', dataUrl, 'user');
      quill.setSelection(index + 1);

      requestAnimationFrame(() => {
        const images = quill.root.querySelectorAll('img');
        const img = images[images.length - 1];
        if (img instanceof HTMLImageElement) {
          img.alt = payload.name;
        }
      });

      this.#sync();
    } catch {
      window.alert('No se pudo insertar la imagen. Inténtalo de nuevo.');
    }
  }

  /**
   * @param {boolean} [notifyChange]
   */
  #sync(notifyChange = true) {
    if (!this.#quill || !this.#output) {
      return;
    }

    /** @type {{ getSemanticHTML?: () => string; root: HTMLElement }} */
    const quill = this.#quill;
    const html = quill.getSemanticHTML?.() ?? quill.root.innerHTML;
    const normalized = !html || html === '<p><br></p>' ? '' : html.trim();
    this.#output.value = normalized;

    if (notifyChange) {
      this.#onChange?.();
    }
  }

  destroy() {
    this.#quill = null;
  }

  /** @returns {string} */
  getHtml() {
    this.#sync();
    return this.#output?.value.trim() ?? '';
  }

  /** @returns {string} */
  getPlainText() {
    if (this.#quill && typeof this.#quill.getText === 'function') {
      return this.#quill.getText().trim();
    }

    return stripHtml(this.getHtml());
  }

  /** @returns {number} */
  getWordCount() {
    return countWords(this.getPlainText());
  }

  reset() {
    if (this.#quill && typeof this.#quill.setContents === 'function') {
      this.#quill.setContents([]);
    }

    if (this.#output) {
      this.#output.value = '';
    }

    this.#imageStore.clear();
    this.#onChange?.();
  }

  /**
   * @param {string} html
   */
  setHtml(html) {
    const safeHtml = typeof html === 'string' ? html.trim() : '';
    const content = safeHtml || '<p><br></p>';

    if (this.#quill && this.#quill.root instanceof HTMLElement) {
      // Preferir innerHTML: get-post a menudo trae texto plano convertido a <p>, no HTML rico.
      this.#quill.root.innerHTML = content;
      this.#sync();
      return;
    }

    if (this.#quill && typeof this.#quill.clipboard?.dangerouslyPasteHTML === 'function') {
      this.#quill.setText('');
      this.#quill.clipboard.dangerouslyPasteHTML(0, content);
      this.#sync();
      return;
    }

    if (this.#output) {
      this.#output.value = safeHtml;
    }

    this.#onChange?.();
  }

  /**
   * @returns {Promise<{ html: string; images: ArticleImagePayload[] }>}
   */
  async exportForSubmit() {
    const html = this.getHtml();
    /** @type {ArticleImagePayload[]} */
    const images = [];
    const seen = new Set();

    const doc = new DOMParser().parseFromString(html, 'text/html');
    for (const img of doc.querySelectorAll('img')) {
      const src = img.getAttribute('src') ?? '';
      if (!src.startsWith('data:image/') || seen.has(src)) {
        continue;
      }

      seen.add(src);
      const stored = this.#imageStore.get(src);
      const parsed = stored ?? parseDataImageUrl(src);
      if (parsed) {
        images.push(parsed);
      }
    }

    return { html, images };
  }

  /**
   * @param {boolean} invalid
   */
  setInvalid(invalid) {
    this.#root?.classList.toggle('is-invalid', invalid);
    const editor = this.#root?.querySelector('.ql-editor');
    if (editor instanceof HTMLElement) {
      editor.toggleAttribute('aria-invalid', invalid);
    }
  }
}

class ArticleCustomSelect {
  /** @type {HTMLElement} */
  #root;

  /** @type {HTMLSelectElement} */
  #select;

  /** @type {HTMLButtonElement} */
  #trigger;

  /** @type {HTMLSpanElement} */
  #label;

  /** @type {HTMLUListElement} */
  #menu;

  /** @type {AbortController} */
  #abort = new AbortController();

  /** @type {(() => void) | null} */
  #onChange = null;

  /**
   * @param {HTMLElement} root
   * @param {{ onChange?: () => void }} [options]
   */
  constructor(root, options = {}) {
    this.#onChange = options.onChange ?? null;
    this.#root = root;
    const select = root.querySelector('select');
    if (!(select instanceof HTMLSelectElement)) {
      throw new Error('ArticleCustomSelect requires a select element');
    }

    this.#select = select;
    this.#select.classList.add('offsider-custom-select__native');
    this.#select.tabIndex = -1;

    const listId = `${select.id || 'select'}-listbox`;

    this.#trigger = document.createElement('button');
    this.#trigger.type = 'button';
    this.#trigger.className = 'offsider-custom-select__trigger';
    this.#trigger.setAttribute('aria-haspopup', 'listbox');
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#trigger.setAttribute('aria-controls', listId);

    const fieldLabel = document.querySelector(`label[for="${select.id}"]`);
    if (fieldLabel instanceof HTMLLabelElement) {
      if (!fieldLabel.id) {
        fieldLabel.id = `${select.id}-label`;
      }
      this.#trigger.setAttribute('aria-labelledby', fieldLabel.id);
    }

    this.#label = document.createElement('span');
    this.#label.className = 'offsider-custom-select__value';
    this.#trigger.appendChild(this.#label);

    const chevron = document.createElement('span');
    chevron.className = 'offsider-custom-select__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.textContent = '▾';
    this.#trigger.appendChild(chevron);

    this.#menu = document.createElement('ul');
    this.#menu.className = 'offsider-custom-select__menu';
    this.#menu.id = listId;
    this.#menu.setAttribute('role', 'listbox');
    this.#menu.hidden = true;

    root.appendChild(this.#trigger);
    root.appendChild(this.#menu);

    const { signal } = this.#abort;

    this.#trigger.addEventListener('click', () => this.#toggle(), { signal });
    this.#trigger.addEventListener('keydown', (event) => this.#onTriggerKeydown(event), { signal });
    this.#menu.addEventListener('keydown', (event) => this.#onMenuKeydown(event), { signal });
    document.addEventListener('click', (event) => this.#onDocumentClick(event), { signal });
    this.#select.addEventListener('change', () => this.#syncLabel(), { signal });

    this.refresh();
  }

  destroy() {
    this.#abort.abort();
  }

  get select() {
    return this.#select;
  }

  refresh() {
    this.#renderOptions();
    this.#syncLabel();
  }

  syncFromNative() {
    this.#syncLabel();
    this.close();
    this.#root.classList.remove('is-invalid');
  }

  close() {
    this.#menu.hidden = true;
    this.#trigger.setAttribute('aria-expanded', 'false');
  }

  /**
   * @param {boolean} invalid
   */
  setInvalid(invalid) {
    this.#root.classList.toggle('is-invalid', invalid);
    this.#select.toggleAttribute('aria-invalid', invalid);
  }

  #toggle() {
    if (this.#menu.hidden) {
      this.#menu.hidden = false;
      this.#trigger.setAttribute('aria-expanded', 'true');
      const active = this.#menu.querySelector('[aria-selected="true"]');
      if (active instanceof HTMLElement) {
        active.focus();
      }
    } else {
      this.close();
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick(event) {
    if (!(event.target instanceof Node) || this.#root.contains(event.target)) return;
    this.close();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.#menu.hidden) {
        this.#toggle();
      }
    }
    if (event.key === 'Escape') {
      this.close();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onMenuKeydown(event) {
    const options = [...this.#menu.querySelectorAll('[role="option"]')];
    const currentIndex = options.findIndex((el) => el === document.activeElement);

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.#trigger.focus();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = options[Math.min(currentIndex + 1, options.length - 1)];
      if (next instanceof HTMLElement) next.focus();
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prev = options[Math.max(currentIndex - 1, 0)];
      if (prev instanceof HTMLElement) prev.focus();
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const option = document.activeElement;
      if (option instanceof HTMLElement && option.dataset.value !== undefined) {
        this.#pick(option.dataset.value);
        this.#trigger.focus();
      }
    }
  }

  #renderOptions() {
    this.#menu.replaceChildren();

    for (const option of this.#select.options) {
      const item = document.createElement('li');
      item.className = 'offsider-custom-select__option';
      item.setAttribute('role', 'option');
      item.tabIndex = -1;
      item.dataset.value = option.value;
      item.setAttribute('aria-selected', option.selected ? 'true' : 'false');

      if (option.dataset.logo) {
        item.classList.add('offsider-custom-select__option--with-logo');
        const logo = document.createElement('img');
        logo.className = 'offsider-custom-select__option-logo';
        logo.src = option.dataset.logo;
        logo.alt = '';
        logo.loading = 'lazy';
        logo.setAttribute('aria-hidden', 'true');
        item.appendChild(logo);
      }

      const label = document.createElement('span');
      label.className = 'offsider-custom-select__option-label';
      label.textContent = option.textContent?.trim() ?? '';
      item.appendChild(label);

      if (option.disabled) {
        item.classList.add('offsider-custom-select__option--disabled');
      }

      item.addEventListener('click', () => {
        if (option.disabled) return;
        this.#pick(option.value);
      });

      this.#menu.appendChild(item);
    }
  }

  /**
   * @param {string} value
   */
  #pick(value) {
    this.#select.value = value;
    this.#select.dispatchEvent(new Event('change', { bubbles: true }));
    this.refresh();
    this.close();
    this.#onChange?.();
  }

  #syncLabel() {
    const selected = this.#select.selectedOptions[0];
    this.#label.replaceChildren();

    if (selected?.dataset.logo) {
      const logo = document.createElement('img');
      logo.className = 'offsider-custom-select__value-logo';
      logo.src = selected.dataset.logo;
      logo.alt = '';
      logo.loading = 'lazy';
      logo.setAttribute('aria-hidden', 'true');
      this.#label.appendChild(logo);
    }

    const text = document.createElement('span');
    text.className = 'offsider-custom-select__value-text';
    text.textContent = selected?.textContent?.trim() ?? '';
    this.#label.appendChild(text);

    this.#root.classList.toggle('is-placeholder', !this.#select.value);
  }
}

class OffsiderArticleSubmitForm extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {ArticleQuillEditor | null} */
  #richEditor = null;

  /** @type {ArticleCustomSelect | null} */
  #categorySelectUi = null;

  /** @type {ArticleCustomSelect | null} */
  #teamSelectUi = null;

  /** @type {Record<string, TeamOption[]>} */
  #teamsByCategory = {};

  /** @type {Record<string, ArticleMeta>} */
  #articleMetaById = {};

  /** @type {string | null} */
  #editingArticleId = null;

  /** @type {'posts' | 'compose'} */
  #activeTab = 'posts';

  /** @type {string | null} */
  #postsFlashMessage = null;

  async connectedCallback() {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    await waitForDependency(() => typeof Quill !== 'undefined', 8000);

    this.#teamsByCategory = this.#loadTeamsMap();
    this.#articleMetaById = this.#loadArticleMetaMap();
    this.#initCustomSelects(form);
    this.#richEditor = this.#initRichEditor(form);
    this.#syncTeamOptions(form);

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });

    this.querySelector('[data-author-posts]')?.addEventListener(
      'click',
      (event) => this.#onAuthorPostsClick(event),
      { signal }
    );

    this.querySelector('[data-edit-cancel]')?.addEventListener(
      'click',
      () => this.#exitEditMode(form, 'posts'),
      { signal }
    );

    this.#initTabs(signal);

    const imagesInput = form.querySelector('input[type="file"][name="images[]"]');
    const imagesLabel = form.querySelector('.offsider-article-submit__file-text');
    const defaultImagesLabel = imagesLabel?.textContent?.trim() ?? '';

    imagesInput?.addEventListener(
      'change',
      () => {
        if (!(imagesInput instanceof HTMLInputElement) || !imagesLabel) return;
        const file = imagesInput.files?.[0];
        if (file) {
          imagesLabel.textContent = file.name;
        } else if (defaultImagesLabel) {
          imagesLabel.textContent = defaultImagesLabel;
        }
      },
      { signal }
    );

    const initialTab = this.dataset.activeTab === 'compose' ? 'compose' : 'posts';
    const hasPostsPanel = Boolean(this.querySelector('[data-tab-panel="posts"]'));
    this.#setActiveTab(hasPostsPanel ? initialTab : 'compose', { focus: false });

    this.#loadAuthorPosts();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#richEditor?.destroy();
    this.#richEditor = null;
    this.#categorySelectUi?.destroy();
    this.#teamSelectUi?.destroy();
    this.#categorySelectUi = null;
    this.#teamSelectUi = null;
  }

  /**
   * @param {HTMLFormElement} form
   */
  #initCustomSelects(form) {
    const categoryRoot = form.querySelector('[data-custom-select="category"]');
    const teamRoot = form.querySelector('[data-custom-select="team"]');

    if (categoryRoot instanceof HTMLElement) {
      this.#categorySelectUi = new ArticleCustomSelect(categoryRoot, {
        onChange: () => {
          this.#syncTeamOptions(form);
        },
      });
    }

    if (teamRoot instanceof HTMLElement) {
      this.#teamSelectUi = new ArticleCustomSelect(teamRoot);
    }
  }

  /**
   * @param {AbortSignal} signal
   */
  #initTabs(signal) {
    const tablist = this.#getTablist();
    if (!(tablist instanceof HTMLElement)) return;

    tablist.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const tab = target.closest('[data-tab]');
        if (!(tab instanceof HTMLElement) || !tablist.contains(tab)) return;

        const name = tab.dataset.tab === 'compose' ? 'compose' : 'posts';
        if (this.#editingArticleId) {
          const form = this.querySelector('form');
          if (form instanceof HTMLFormElement) {
            this.#exitEditMode(form, name);
            return;
          }
        }
        this.#setActiveTab(name, { focus: true });
      },
      { signal }
    );

    tablist.addEventListener(
      'keydown',
      (event) => {
        if (!(event instanceof KeyboardEvent)) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Home' && event.key !== 'End') {
          return;
        }

        const tabs = [...tablist.querySelectorAll('[data-tab]')].filter(
          (el) => el instanceof HTMLElement && !el.hidden
        );
        if (tabs.length === 0) return;

        const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        let next = current;
        if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;

        const nextTab = tabs[next];
        if (!(nextTab instanceof HTMLElement)) return;
        event.preventDefault();
        const name = nextTab.dataset.tab === 'compose' ? 'compose' : 'posts';
        this.#setActiveTab(name, { focus: true });
      },
      { signal }
    );
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getTablist() {
    const account = this.closest('.offsider-article-submit__account');
    const tablist = account?.querySelector('[data-account-tabs]');
    return tablist instanceof HTMLElement ? tablist : null;
  }

  /**
   * @param {'posts' | 'compose'} tab
   * @param {{ focus?: boolean }} [options]
   */
  #setActiveTab(tab, options = {}) {
    const hasPosts = Boolean(this.querySelector('[data-tab-panel="posts"]'));
    const nextTab = tab === 'posts' && hasPosts ? 'posts' : 'compose';
    this.#activeTab = nextTab;
    this.dataset.activeTab = nextTab;

    this.querySelectorAll('[data-tab-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isActive = panel.dataset.tabPanel === nextTab;
      panel.hidden = !isActive;
      if (isActive && options.focus) {
        panel.focus({ preventScroll: true });
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    const tablist = this.#getTablist();
    if (!(tablist instanceof HTMLElement)) return;

    tablist.querySelectorAll('[data-tab]').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      const isActive = btn.dataset.tab === nextTab;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.tabIndex = isActive ? 0 : -1;
      if (isActive && options.focus) {
        btn.focus();
      }
    });
  }

  /**
   * @returns {Record<string, TeamOption[]>}
   */
  #loadTeamsMap() {
    const jsonEl = this.querySelector('[data-category-teams-map]');
    if (!jsonEl?.textContent?.trim()) {
      return {};
    }

    try {
      const parsed = JSON.parse(jsonEl.textContent);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * Metadatos locales del blog (url + body) indexados por ID numérico de artículo.
   * El listado de la API solo trae id/title; el cuerpo se hidrata desde aquí o vía /.json.
   * @returns {Record<string, ArticleMeta>}
   */
  #loadArticleMetaMap() {
    const jsonEl = this.querySelector('[data-article-url-map]');
    if (!jsonEl?.textContent?.trim()) {
      return {};
    }

    try {
      const parsed = JSON.parse(jsonEl.textContent);
      if (!parsed || typeof parsed !== 'object') {
        return {};
      }

      /** @type {Record<string, ArticleMeta>} */
      const map = {};
      for (const [key, value] of Object.entries(parsed)) {
        const id = String(key);
        if (typeof value === 'string' && value) {
          map[id] = { url: value };
          continue;
        }
        if (value && typeof value === 'object') {
          /** @type {ArticleMeta} */
          const meta = {};
          if (typeof value.url === 'string' && value.url) meta.url = value.url;
          if (typeof value.title === 'string' && value.title) meta.title = value.title;
          if (typeof value.body === 'string' && value.body) meta.body = value.body;
          map[id] = meta;
        }
      }
      return map;
    } catch {
      return {};
    }
  }

  /**
   * @param {string} articleId
   * @returns {ArticleMeta}
   */
  #resolveArticleMeta(articleId) {
    const numericId = extractArticleNumericId(articleId) || articleId;
    return this.#articleMetaById[numericId] ?? this.#articleMetaById[articleId] ?? {};
  }

  async #loadAuthorPosts() {
    const root = this.querySelector('[data-author-posts]');
    if (!(root instanceof HTMLElement)) {
      return;
    }

    const listEl = root.querySelector('[data-author-posts-list]');
    const statusEl = root.querySelector('[data-author-posts-status]');
    const countEl = root.querySelector('[data-author-posts-count]');
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement) || !(listEl instanceof HTMLElement)) {
      return;
    }

    const endpoint = this.dataset.postsEndpoint?.trim() || DEFAULT_POSTS_ENDPOINT;
    const storeId = this.dataset.shopId?.trim();
    const authorName = this.#resolveAuthorName(form);

    if (!storeId || !authorName) {
      this.#renderAuthorPostsStatus(
        statusEl,
        listEl,
        countEl,
        this.dataset.postsError?.trim() || 'No se pudieron cargar tus artículos.',
        'error'
      );
      return;
    }

    this.#renderAuthorPostsStatus(
      statusEl,
      listEl,
      countEl,
      this.dataset.postsLoading?.trim() || 'Cargando tus artículos…',
      'pending'
    );

    try {
      const hash = await buildAuthorHash(authorName, storeId);
      const response = await fetchGetWithQuery(
        endpoint,
        { hash, authorName },
        this.#abort?.signal
      );

      const responseData = await parseJsonResponse(response);
      if (!response.ok) {
        throwApiError(responseData, response.status);
      }

      const posts = Array.isArray(responseData.posts) ? responseData.posts : [];
      this.#cachePostsMeta(/** @type {AuthorPost[]} */ (posts));
      this.#renderAuthorPosts(listEl, statusEl, countEl, /** @type {AuthorPost[]} */ (posts));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      const message = formatFetchError(
        error,
        this.dataset.postsError?.trim() || 'No se pudieron cargar tus artículos.'
      );
      this.#renderAuthorPostsStatus(statusEl, listEl, countEl, message, 'error');
    }
  }

  /**
   * Si posts-by-author trae body, lo guardamos para editar sin depender de get-post (CORS).
   * @param {AuthorPost[]} posts
   */
  #cachePostsMeta(posts) {
    for (const post of posts) {
      const articleId = typeof post.id === 'string' ? post.id : typeof post.articleId === 'string' ? post.articleId : '';
      const numericId = extractArticleNumericId(articleId) || articleId;
      if (!numericId) continue;

      const current = this.#articleMetaById[numericId] ?? {};
      /** @type {ArticleMeta} */
      const next = { ...current };
      if (typeof post.title === 'string' && post.title.trim()) {
        next.title = post.title.trim();
      }
      if (typeof post.url === 'string' && post.url) {
        next.url = post.url;
      }
      if (typeof post.body === 'string' && post.body.trim()) {
        next.body = post.body;
      }
      this.#articleMetaById[numericId] = next;
    }
  }

  /**
   * @param {string} message
   * @param {'success' | 'error' | 'pending'} [_state]
   */
  #showPostsFlash(message, _state = 'success') {
    this.#postsFlashMessage = message;
  }

  /**
   * @param {Element | null} statusEl
   * @param {HTMLElement} listEl
   * @param {Element | null} countEl
   * @param {string} message
   * @param {'pending' | 'error' | 'empty'} state
   */
  #renderAuthorPostsStatus(statusEl, listEl, countEl, message, state) {
    listEl.hidden = true;
    listEl.replaceChildren();

    if (countEl instanceof HTMLElement) {
      countEl.hidden = true;
      countEl.textContent = '';
    }

    if (statusEl instanceof HTMLElement) {
      statusEl.hidden = false;
      statusEl.textContent = message;
      statusEl.dataset.state = state;
    }
  }

  /**
   * @param {HTMLElement} listEl
   * @param {Element | null} statusEl
   * @param {Element | null} countEl
   * @param {AuthorPost[]} posts
   */
  #renderAuthorPosts(listEl, statusEl, countEl, posts) {
    if (posts.length === 0) {
      this.#renderAuthorPostsStatus(
        statusEl,
        listEl,
        countEl,
        this.#postsFlashMessage || this.dataset.postsEmpty?.trim() || 'Aún no tienes artículos.',
        this.#postsFlashMessage ? 'success' : 'empty'
      );
      this.#postsFlashMessage = null;
      return;
    }

    if (statusEl instanceof HTMLElement) {
      if (this.#postsFlashMessage) {
        statusEl.hidden = false;
        statusEl.textContent = this.#postsFlashMessage;
        statusEl.dataset.state = 'success';
        this.#postsFlashMessage = null;
      } else {
        statusEl.hidden = true;
        statusEl.textContent = '';
        statusEl.removeAttribute('data-state');
      }
    }

    if (countEl instanceof HTMLElement) {
      countEl.hidden = false;
      const label = this.dataset.postsCountLabel?.trim() || '__COUNT__ artículos';
      countEl.textContent = label.replace(/__COUNT__/g, String(posts.length));
    }

    const viewLabel = this.dataset.postsViewLabel?.trim() || 'Ver';
    const editLabel = this.dataset.postsEditLabel?.trim() || 'Editar';
    const draftLabel = this.dataset.postsDraftLabel?.trim() || 'Sin enlace público';
    const publishedLabel = this.dataset.postsPublishedLabel?.trim() || 'Publicado';
    const fragment = document.createDocumentFragment();

    for (const post of posts) {
      const title = typeof post.title === 'string' ? post.title.trim() : '';
      if (!title) {
        continue;
      }

      const articleId = typeof post.id === 'string' ? post.id.trim() : '';
      if (!articleId) {
        continue;
      }

      const item = document.createElement('li');
      item.className = 'offsider-article-submit__posts-item';
      item.setAttribute('role', 'listitem');

      const metaLocal = this.#resolveArticleMeta(articleId);
      const url =
        (typeof post.url === 'string' && post.url) ||
        metaLocal.url ||
        '';

      const card = document.createElement('article');
      card.className = 'offsider-article-submit__posts-card';

      const body = document.createElement('div');
      body.className = 'offsider-article-submit__posts-card-body';

      const titleEl = document.createElement('h3');
      titleEl.className = 'offsider-article-submit__posts-card-title';
      titleEl.textContent = title;

      const meta = document.createElement('p');
      meta.className = 'offsider-article-submit__posts-card-meta';
      meta.textContent = url ? publishedLabel : draftLabel;

      body.append(titleEl, meta);
      card.appendChild(body);

      const actions = document.createElement('div');
      actions.className = 'offsider-article-submit__posts-card-actions';

      const editBtn = document.createElement('button');
      editBtn.type = 'button';
      editBtn.className =
        'offsider-article-submit__posts-card-action offsider-article-submit__posts-card-action--edit';
      editBtn.textContent = editLabel;
      editBtn.dataset.editArticle = articleId;
      editBtn.dataset.editTitle = title;
      if (url) {
        editBtn.dataset.editUrl = url;
      }
      actions.appendChild(editBtn);

      if (url) {
        const action = document.createElement('a');
        action.className = 'offsider-article-submit__posts-card-action';
        action.href = url;
        action.textContent = viewLabel;
        actions.appendChild(action);
      }

      card.appendChild(actions);
      item.appendChild(card);
      fragment.appendChild(item);
    }

    listEl.replaceChildren(fragment);
    listEl.hidden = false;
  }

  /**
   * @param {Event} event
   */
  #onAuthorPostsClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const editBtn = target.closest('[data-edit-article]');
    if (!(editBtn instanceof HTMLElement)) return;

    event.preventDefault();
    const articleId = editBtn.dataset.editArticle?.trim();
    if (!articleId) return;

    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    void this.#enterEditMode(form, {
      articleId,
      title: editBtn.dataset.editTitle?.trim() || '',
      url: editBtn.dataset.editUrl?.trim() || '',
    });
  }

  /**
   * @param {HTMLFormElement} form
   * @param {{ articleId: string; title: string; url?: string }} post
   */
  async #enterEditMode(form, post) {
    this.#editingArticleId = post.articleId;
    this.dataset.editMode = 'true';
    this.#setActiveTab('compose', { focus: false });
    this.#syncEditUi(form, true);

    const titleInput = form.querySelector('[name="article_title"]');
    if (titleInput instanceof HTMLInputElement) {
      titleInput.value = post.title;
    }

    const statusEl = form.querySelector('[data-article-submit-status]');
    this.#showStatus(statusEl, this.dataset.editLoading?.trim() || 'Cargando artículo…', 'pending');

    const storeId = this.dataset.shopId?.trim();
    const authorName = this.#resolveAuthorName(form);
    const localMeta = this.#resolveArticleMeta(post.articleId);
    let title = (typeof localMeta.title === 'string' && localMeta.title) || post.title;
    let bodyHtml = localMeta.body ? articleBodyToHtml(localMeta.body) : '';
    /** @type {string | null} */
    let loadErrorMessage = null;

    try {
      if (!storeId || !authorName) {
        throw new Error(this.dataset.editLoadError?.trim() || 'No se pudo cargar el artículo.');
      }

      const hash = await buildAuthorHash(authorName, storeId);
      const endpoint = this.dataset.getPostEndpoint?.trim() || DEFAULT_GET_POST_ENDPOINT;
      const response = await fetchGetWithQuery(
        endpoint,
        {
          hash,
          articleId: post.articleId,
          authorName,
        },
        this.#abort?.signal
      );

      const responseData = await parseJsonResponse(response);
      if (!response.ok) {
        throwApiError(responseData, response.status);
      }

      const article = pickArticlePayload(responseData);

      if (typeof article.title === 'string' && article.title.trim()) {
        title = article.title.trim();
      }

      const remoteBody = readArticleBody(article);
      if (remoteBody) {
        bodyHtml = remoteBody;
      }

      const returnedId =
        (typeof article.articleId === 'string' && article.articleId) ||
        (typeof article.id === 'string' && article.id) ||
        '';
      if (returnedId) {
        this.#editingArticleId = returnedId;
      }

      const numericId = extractArticleNumericId(returnedId || post.articleId) || post.articleId;
      this.#articleMetaById[numericId] = {
        ...(this.#articleMetaById[numericId] ?? {}),
        title,
        body: typeof article.body === 'string' ? article.body : bodyHtml,
        url: localMeta.url,
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      loadErrorMessage = formatFetchError(
        error,
        this.dataset.editLoadError?.trim() || 'No se pudo cargar el artículo.'
      );
      if (!bodyHtml && (post.url || localMeta.url)) {
        const fetched = (await this.#fetchArticleBodyFromUrl(post.url || localMeta.url || '')) || '';
        bodyHtml = fetched ? articleBodyToHtml(fetched) : '';
      }
    }

    if (titleInput instanceof HTMLInputElement) {
      titleInput.value = title;
    }

    try {
      this.#richEditor?.setHtml(bodyHtml);
    } catch {
      const output = form.querySelector('[data-rich-editor-output]');
      if (output instanceof HTMLTextAreaElement) {
        output.value = bodyHtml;
      }
    }
    this.#syncEditUi(form, Boolean(bodyHtml));

    requestAnimationFrame(() => {
      const compose = this.querySelector('[data-tab-panel="compose"]');
      if (compose instanceof HTMLElement) {
        compose.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      const focusTarget = form.querySelector('[name="article_title"]');
      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus();
      }
    });

    if (bodyHtml) {
      // Contenido disponible (API o caché local): no bloquear por CORS de get-post.
      if (statusEl instanceof HTMLElement) {
        statusEl.hidden = true;
        statusEl.textContent = '';
        statusEl.removeAttribute('data-state');
      }
      return;
    }

    this.#showStatus(
      statusEl,
      loadErrorMessage ||
        this.dataset.editBodyMissing?.trim() ||
        'No pudimos cargar el texto del artículo. Pégalo en el editor antes de guardar o podrías sobrescribir el contenido.',
      'pending'
    );
  }

  /**
   * @param {string} url
   * @returns {Promise<string>}
   */
  async #fetchArticleBodyFromUrl(url) {
    if (!url) return '';

    try {
      const jsonUrl = url.includes('.json') ? url : `${url.split('?')[0]}.json`;
      const response = await fetch(jsonUrl, {
        headers: { Accept: 'application/json' },
        signal: this.#abort?.signal,
      });
      if (!response.ok) return '';

      const data = await response.json();
      const article = data?.article ?? data;
      if (typeof article?.body_html === 'string' && article.body_html.trim()) {
        return article.body_html;
      }
      if (typeof article?.content === 'string' && article.content.trim()) {
        return article.content;
      }
    } catch {
      // Silenciar: el autor puede pegar el cuerpo manualmente.
    }

    return '';
  }

  /**
   * @param {HTMLFormElement} form
   * @param {'posts' | 'compose'} [returnTab]
   */
  #exitEditMode(form, returnTab = 'posts') {
    this.#editingArticleId = null;
    delete this.dataset.editMode;

    form.reset();
    this.#richEditor?.reset();
    this.#categorySelectUi?.syncFromNative();
    this.#teamSelectUi?.syncFromNative();
    this.#syncTeamOptions(form);
    this.#clearFieldErrors(form);
    this.#syncEditUi(form, true);
    this.#setActiveTab(returnTab, { focus: false });

    const statusEl = form.querySelector('[data-article-submit-status]');
    if (statusEl instanceof HTMLElement) {
      statusEl.hidden = true;
      statusEl.textContent = '';
      statusEl.removeAttribute('data-state');
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} [_bodyLoaded]
   */
  #syncEditUi(form, _bodyLoaded = true) {
    const editing = Boolean(this.#editingArticleId);
    const createHeading = this.dataset.formHeading?.trim() || 'Nuevo artículo';
    const editHeading = this.dataset.editHeading?.trim() || 'Editar artículo';
    const createLabel = this.dataset.submitLabel?.trim() || 'Enviar artículo';
    const updateLabel = this.dataset.updateLabel?.trim() || 'Guardar cambios';

    const heading = this.querySelector('[data-compose-heading]');
    if (heading instanceof HTMLElement) {
      heading.textContent = editing ? editHeading : createHeading;
    }

    const kicker = this.querySelector('[data-compose-kicker]');
    if (kicker instanceof HTMLElement) {
      kicker.hidden = editing;
    }

    const cancelBtn = this.querySelector('[data-edit-cancel]');
    if (cancelBtn instanceof HTMLElement) {
      cancelBtn.hidden = !editing;
    }

    const tablist = this.#getTablist();
    if (tablist instanceof HTMLElement) {
      tablist.hidden = editing;
    }

    const submitLabel = form.querySelector('[data-submit-label]');
    if (submitLabel instanceof HTMLElement) {
      submitLabel.textContent = editing ? updateLabel : createLabel;
    }

    form.querySelectorAll('[data-create-only]').forEach((el) => {
      if (el instanceof HTMLElement) {
        el.hidden = editing;
        el.querySelectorAll('input, select, textarea').forEach((field) => {
          if (
            field instanceof HTMLInputElement ||
            field instanceof HTMLSelectElement ||
            field instanceof HTMLTextAreaElement
          ) {
            if (editing) {
              field.dataset.wasRequired = field.required ? 'true' : 'false';
              field.required = false;
            } else if (field.dataset.wasRequired === 'true') {
              field.required = true;
            }
          }
        });
      }
    });

    if (!editing) {
      this.#syncTeamOptions(form);
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  #initRichEditor(form) {
    const editorRoot = form.querySelector('[data-rich-editor]');
    if (!(editorRoot instanceof HTMLElement)) return null;

    /** @type {ArticleQuillEditor | null} */
    let editor = null;

    const maxImageMb = Number.parseFloat(this.dataset.maxImageMb ?? '5');
    const maxImageBytes = Number.isFinite(maxImageMb) && maxImageMb > 0 ? maxImageMb * 1024 * 1024 : DEFAULT_MAX_IMAGE_BYTES;

    editor = new ArticleQuillEditor(editorRoot, {
      onChange: () => {
        editor?.setInvalid(false);
      },
      maxImageBytes,
      imageTooLargeMessage: this.dataset.imageTooLargeMessage?.trim() || undefined,
    });

    return editor;
  }

  /**
   * @param {HTMLFormElement} form
   */
  #syncTeamOptions(form) {
    const categorySelect = this.#categorySelectUi?.select ?? form.querySelector('[name="category"]');
    const teamField = form.querySelector('[data-team-field]');
    const teamSelect = this.#teamSelectUi?.select ?? form.querySelector('[name="team_tag"]');

    if (!(categorySelect instanceof HTMLSelectElement) || !(teamSelect instanceof HTMLSelectElement)) {
      return;
    }

    const placeholder = teamSelect.dataset.placeholder ?? teamSelect.options[0]?.textContent ?? '';
    const teams = this.#readTeamsForCategory(categorySelect.value);

    teamSelect.replaceChildren();

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = placeholder;
    defaultOption.selected = true;
    teamSelect.appendChild(defaultOption);

    for (const team of teams) {
      const option = document.createElement('option');
      option.value = team.tag;
      option.textContent = team.title;
      if (team.logo) {
        option.dataset.logo = team.logo;
      }
      teamSelect.appendChild(option);
    }

    this.#teamSelectUi?.refresh();

    if (teamField instanceof HTMLElement) {
      teamField.hidden = teams.length === 0;
    }
  }

  /**
   * @param {string} categoryHandle
   * @returns {TeamOption[]}
   */
  #readTeamsForCategory(categoryHandle) {
    if (!categoryHandle) {
      return [];
    }

    const teams = this.#teamsByCategory[categoryHandle];
    return Array.isArray(teams) ? teams : [];
  }

  /**
   * @param {SubmitEvent} event
   */
  async #onSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const statusEl = form.querySelector('[data-article-submit-status]');
    const isEdit = Boolean(this.#editingArticleId);
    const endpoint = isEdit
      ? this.dataset.updateEndpoint?.trim() || DEFAULT_UPDATE_ENDPOINT
      : this.dataset.apiEndpoint?.trim() || DEFAULT_ENDPOINT;
    const storeId = this.dataset.shopId?.trim();

    this.#clearFieldErrors(form);
    this.#richEditor?.setInvalid(false);
    this.#categorySelectUi?.setInvalid(false);
    this.#teamSelectUi?.setInvalid(false);

    const errors = this.#validate(form);
    if (errors.length > 0) {
      this.#showStatus(statusEl, errors.join(' '), 'error');
      const firstInvalid = form.querySelector(
        '[aria-invalid="true"], .is-invalid .offsider-custom-select__trigger, .ql-editor[aria-invalid="true"]'
      );
      if (firstInvalid instanceof HTMLElement) {
        firstInvalid.focus();
      }
      return;
    }

    if (!storeId) {
      this.#showStatus(statusEl, 'No se pudo identificar la tienda. Contacta con soporte.', 'error');
      return;
    }

    const blogTitle = this.dataset.blogTitle?.trim();
    if (!isEdit && !blogTitle) {
      this.#showStatus(statusEl, 'Falta configurar el blog de destino en los ajustes del tema.', 'error');
      return;
    }

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }

    try {
      const payload = isEdit
        ? await this.#buildUpdatePayload(form, storeId, this.#editingArticleId || '')
        : await this.#buildPayload(form, storeId, blogTitle || '');
      const response = await fetch(endpoint, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        mode: 'cors',
      });

      const responseData = await parseJsonResponse(response);
      if (!response.ok) {
        throwApiError(responseData, response.status);
      }

      const successMessage = isEdit
        ? this.dataset.updateSuccess?.trim() || 'Cambios guardados correctamente.'
        : typeof responseData.message === 'string'
          ? responseData.message
          : 'Artículo enviado correctamente. Gracias.';

      if (isEdit) {
        this.#exitEditMode(form, 'posts');
        this.#showPostsFlash(successMessage, 'success');
      } else {
        form.reset();
        this.#richEditor?.reset();
        this.#categorySelectUi?.syncFromNative();
        this.#teamSelectUi?.syncFromNative();
        this.#syncTeamOptions(form);
        const imagesLabel = form.querySelector('.offsider-article-submit__file-text');
        if (imagesLabel && this.dataset.imagesLabel) {
          imagesLabel.textContent = this.dataset.imagesLabel;
        }
        this.#showStatus(statusEl, successMessage, 'success');
      }

      this.#loadAuthorPosts();
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'No se pudo enviar el artículo. Inténtalo de nuevo en unos minutos.';
      this.#showStatus(statusEl, message, 'error');
    } finally {
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
      }
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} storeId
   * @param {string} blogTitle
   */
  /**
   * Tags Shopify: categoría (deporte/liga) y, si hay equipo, un segundo tag.
   * Ej. LaLiga EA + Real Madrid → ["laliga-ea-sports", "real-madrid"]
   *
   * @param {string} categoryHandle
   * @param {string} teamTag
   * @returns {string[]}
   */
  #buildArticleTags(categoryHandle, teamTag) {
    const tags = [];

    if (categoryHandle) {
      tags.push(categoryHandle);
    }

    if (teamTag && teamTag !== categoryHandle) {
      tags.push(teamTag);
    }

    return tags;
  }

  async #buildPayload(form, storeId, blogTitle) {
    const authorName = this.#resolveAuthorName(form);
    const title = this.#value(form, 'article_title');
    const exported = this.#richEditor
      ? await this.#richEditor.exportForSubmit()
      : { html: this.#getArticleBody(form), images: [] };
    const body = exported.html;
    const coverImages = await this.#collectFileInputImages(form);
    const category = this.#value(form, 'category');
    const teamTag = this.#value(form, 'team_tag');
    const socialMedia = this.#value(form, 'social_links');

    const tags = this.#buildArticleTags(category, teamTag);
    const plainBody = stripHtml(body);
    const image = this.#resolveCoverImage(coverImages, body);
    const hash = await buildAuthorHash(authorName, storeId);

    return {
      hash,
      title,
      body,
      metaTitle: title,
      metaDescription: truncateText(plainBody.replace(/\s+/g, ' '), 160),
      authorName,
      category: category || null,
      tags: tags.length > 0 ? tags : null,
      templateSuffix: this.dataset.templateSuffix?.trim() || null,
      blogTitle,
      commentPolicy: this.dataset.commentPolicy?.trim() || 'OPEN',
      summary: truncateText(plainBody.replace(/\s+/g, ' '), 200) || null,
      socialMedia: socialMedia || null,
      image,
    };
  }

  /**
   * Payload mínimo para update-post: hash, articleId, title, body, authorName.
   *
   * @param {HTMLFormElement} form
   * @param {string} storeId
   * @param {string} articleId
   */
  async #buildUpdatePayload(form, storeId, articleId) {
    const authorName = this.#resolveAuthorName(form);
    const title = this.#value(form, 'article_title');
    const exported = this.#richEditor
      ? await this.#richEditor.exportForSubmit()
      : { html: this.#getArticleBody(form), images: [] };
    const body = exported.html;
    const hash = await buildAuthorHash(authorName, storeId);

    return {
      hash,
      articleId,
      title,
      body,
      authorName,
    };
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {Promise<ArticleImagePayload[]>}
   */
  async #collectFileInputImages(form) {
    const input = form.querySelector('input[type="file"][name="images[]"]');
    if (!(input instanceof HTMLInputElement) || !input.files?.length) {
      return [];
    }

    const maxImageMb = Number.parseFloat(this.dataset.maxImageMb ?? '5');
    const maxImageBytes = Number.isFinite(maxImageMb) && maxImageMb > 0 ? maxImageMb * 1024 * 1024 : DEFAULT_MAX_IMAGE_BYTES;
    /** @type {ArticleImagePayload[]} */
    const images = [];

    for (const file of input.files) {
      if (!file.type.startsWith('image/')) {
        continue;
      }

      if (file.size > maxImageBytes) {
        throw new Error(this.dataset.imageTooLargeMessage?.trim() || 'Alguna imagen supera el tamaño máximo permitido.');
      }

      images.push(await fileToImagePayload(file));
    }

    return images;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  #getArticleBody(form) {
    if (this.#richEditor) {
      return this.#richEditor.getHtml();
    }

    const bodyField = form.querySelector('[name="article_body"]');
    if (bodyField instanceof HTMLTextAreaElement) {
      return bodyField.value.trim();
    }

    return '';
  }

  /**
   * Imagen de portada (`image` en el API): prioriza el archivo subido en el formulario.
   * Las imágenes incrustadas en el rich text solo van en `body`, no en `image`.
   *
   * @param {ArticleImagePayload[]} coverImages
   * @param {string} body
   * @returns {string | null}
   */
  #resolveCoverImage(coverImages, body) {
    if (coverImages.length > 0) {
      return `data:${coverImages[0].type};base64,${coverImages[0].data}`;
    }

    return extractFirstImageUrl(body);
  }

  /**
   * @param {HTMLFormElement} form
   */
  #resolveAuthorName(form) {
    const fromDataset = this.dataset.hashAuthorName?.trim();
    if (fromDataset) {
      return fromDataset;
    }
    return this.#value(form, 'author_name');
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   */
  #value(form, name) {
    const field = form.elements.namedItem(name);
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
      return field.value.trim();
    }
    return '';
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {string[]}
   */
  #validate(form) {
    /** @type {string[]} */
    const errors = [];

    if (this.#richEditor) {
      this.#richEditor.getHtml();
    }

    const requiredFields = form.querySelectorAll('[required]');
    requiredFields.forEach((field) => {
      if (
        !(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)
      ) {
        return;
      }

      let empty = field.type === 'checkbox' ? !field.checked : !String(field.value).trim();

      if (field instanceof HTMLTextAreaElement && field.name === 'article_body' && this.#richEditor) {
        empty = !this.#richEditor.getPlainText();
      }

      if (empty) {
        field.setAttribute('aria-invalid', 'true');
        if (field.name === 'category') {
          this.#categorySelectUi?.setInvalid(true);
        }
        errors.push('Completa todos los campos obligatorios.');
      }
    });

    const minWords = Number.parseInt(this.dataset.minWords ?? '0', 10);
    const bodyText = this.#richEditor ? this.#richEditor.getPlainText() : this.#getArticleBody(form);
    const words = countWords(bodyText);

    if (minWords > 0 && words < minWords) {
      this.#richEditor?.setInvalid(true);
      const surface = form.querySelector('.ql-editor');
      if (surface instanceof HTMLElement) {
        surface.focus();
      }
      errors.push(`El artículo debe tener al menos ${minWords} palabras.`);
    }

    return [...new Set(errors)];
  }

  /**
   * @param {HTMLFormElement} form
   */
  #clearFieldErrors(form) {
    form.querySelectorAll('[aria-invalid="true"]').forEach((field) => {
      field.removeAttribute('aria-invalid');
    });
    form.querySelectorAll('.offsider-custom-select.is-invalid, [data-rich-editor].is-invalid').forEach((el) => {
      el.classList.remove('is-invalid');
    });
  }

  /**
   * @param {Element | null} statusEl
   * @param {string} message
   * @param {'error' | 'success' | 'pending'} state
   */
  #showStatus(statusEl, message, state) {
    if (!(statusEl instanceof HTMLElement)) return;
    statusEl.textContent = message;
    statusEl.dataset.state = state;
    statusEl.hidden = false;
    statusEl.setAttribute('tabindex', '-1');
    statusEl.focus();
  }
}

if (!customElements.get('offsider-article-submit-form')) {
  customElements.define('offsider-article-submit-form', OffsiderArticleSubmitForm);
}
